import { useNavigate, useParams } from 'react-router-dom';
import { useToastError } from '~/utils/useToastError';
import { useFormik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { InputBar, Button, QuerySelect } from '~/ui';
import styles from './EditQuestionnarieForm.module.scss';
import QuestionsList from '~/components/QuestionnaireForm/QuestionsList';
import { instance } from '~/utils/api/api';
import { useEffect, useState } from 'react';
import { Questionnaire } from '~/pages/Questionnaires/QuestionnairesBoard/QuestionnairesTable/QuestionnairesTable';
import QuestionForm, { QuestionItem } from '~/components/QuestionForm';
import { Modal } from '~/components/Shared/Modal/Modal';
import { getQuestionPayload } from './utils';
import EditQuestionForm, {
  QuestionValues,
} from '~/components/EditQuestionForm/EditQuestionForm';
import StatIcon from '~/assets/svg/questionnaries/stat-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { useToastSuccess } from '~/utils/useToastSuccess';
import CustomQuerySelect from '~/components/Shared/CustomQuerySelect/CustomQuerySelect';
import { useQueryClient } from '@tanstack/react-query';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import CustomCheckbox from '~/components/Shared/CustomCheckbox/CustomCheckbox';
import { PaginatedResponse } from '~/utils/types/common';
import { UserItem } from '~/components/UserForm';
import { getUsers } from '~/utils/api/queries';
import { useDebounce } from '@uidotdev/usehooks';
import { validate } from '~/utils/validateQuestionnaires';
import { getPaginate } from '~/utils/getPaginate';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';

interface ITypeOption {
  id: number;
  name: string;
}

interface IFormikProps {
  name: string;
  type: {
    id: number;
    name: string;
  };
  publication_date: null | Dayjs;
  removal_date: null | Dayjs;
  licensor: string;
  group_send: string;
  for_novice: boolean;
  questionnaire_accommodations: { id: number; name: string }[];
  questionnaire_organizations: { id: number; name: string }[];
  accommodation_all: boolean;
  organization_all: boolean;
  user_id: { id: number; name: string }[];
  show_name: boolean;
  show_phone: boolean;
}

export interface QuestionnairePayload {
  name: string;
  licensor: string;
  questionnaire_accommodations: { id: number; name: string }[];
  questionnaire_organizations: { id: number; name: string }[];
  publication_date: Dayjs | null;
  removal_date: Dayjs | null;
  for_novice: boolean;
  type: {
    id: number;
    name: string;
  };
  accommodation_all: boolean;
  organization_all: boolean;
  user_id: { id: number; name: string }[];
}

interface IEditQuestionnarieFormProps {
  questionnaire: Questionnaire;
}

const EditQuestionnarieForm = ({
  questionnaire,
}: IEditQuestionnarieFormProps) => {
  const [typeOptions, setTypeOptions] = useState<ITypeOption[] | []>([]);
  const navigate = useNavigate();
  const { questionnaireID } = useParams();
  const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);
  const [isEditQuestionModalOpen, setIsEditQuestionModalOpen] = useState(false);
  const [questions, setQuestions] = useState<any[]>(
    questionnaire?.questions.sort(
      (a: ITypeOption, b: ITypeOption) => a.id - b.id
    ) || []
  );
  const [selectedUsers, setSelectedUsers] = useState<number[] | []>([]);
  const [searchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [editingQuestion, setEditingQuestion] = useState<QuestionItem | null>(
    null
  );
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();

  const params = {
    sort: '',
    perPage: 100,
    field: '',
    query: debouncedSearchTerm,
    page: 1,
  };

  const initialValues = {
    name: '',
    type: {
      id: 0,
      name: '',
    },
    publication_date: null,
    removal_date: null,
    licensor: '',
    group_send: '1',
    for_novice: false,
    questionnaire_accommodations: [],
    questionnaire_organizations: [],
    accommodation_all: false,
    organization_all: false,
    user_id: [],
    show_name: false,
    show_phone: false,
  };

  const {
    values,
    errors,
    touched,
    isValid,
    isSubmitting,
    setValues,
    resetForm,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    handleSubmit,
    handleChange,
  } = useFormik<IFormikProps>({
    initialValues,
    validate,
    onSubmit: () => {
      handleFormSubmit(values);
    },
  });

  const handleOpenAddQuestionModal = () => {
    setIsAddQuestionModalOpen(true);
  };

  const handleCloseAddQuestionModal = () => {
    setIsAddQuestionModalOpen(false);
  };

  const handleOpenEditQuestionModal = (id: number) => {
    setEditingQuestion(questions.find(question => question.id === id));
    setIsEditQuestionModalOpen(true);
  };

  const handleCloseEditQuestionModal = () => {
    setIsEditQuestionModalOpen(false);
  };

  const getUserById = async (userID: number): Promise<UserItem> => {
    const response = await instance.get(`admin/users/${userID}`);
    return response.data.users.data;
  };

  const loadTypeOptions = async () => {
    try {
      const response = await instance.get('admin/questionnaires-type');
      setTypeOptions(response.data);
    } catch (error) {
      toastError(error);
    }
  };

  const handleFormSubmit = async (values: IFormikProps) => {
    try {
      const {
        name,
        publication_date,
        removal_date,
        type,
        licensor,
        questionnaire_accommodations,
        questionnaire_organizations,
        for_novice,
        accommodation_all,
        organization_all,
        show_name,
        show_phone,
      } = values;

      const accommodation_ids = questionnaire_accommodations.map(
        accommodation => accommodation.id
      );
      const organization_ids = questionnaire_organizations.map(
        organization => organization.id
      );

      const payload = {
        name,
        for_novice,
        name_is_required: show_name,
        phone_is_required: show_phone,
        type_id: type.id,
        publication_date: dayjs(publication_date)
          .hour(0)
          .minute(0)
          .second(1)
          .format('YYYY-MM-DD HH:mm:ss'),
        removal_date: dayjs(removal_date)
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        licensor: licensor,
        ...(accommodation_all ? {} : { accommodation_ids }),
        ...(organization_all ? {} : { organization_ids }),
        ...(accommodation_all ? { accommodation_all } : {}),
        ...(organization_all ? { organization_all } : {}),
        user_ids: selectedUsers,
      };
      await instance.put(`admin/questionnaires/${questionnaireID}`, payload);
      client.invalidateQueries(['questionnaires']);
      client.invalidateQueries(['questionnaire', questionnaireID]);
      resetForm({ values: initialValues });
      navigate(`/questionnaires?${value}=${paginate}`);
      toastSuccess(t('questionnaire_edited'));
    } catch (error) {
      toastError(error);
    }
  };

  const handleCreateQuestion = async (values: QuestionValues) => {
    const payload = getQuestionPayload(values, Number(questionnaireID), true);
    try {
      const response = await instance.post('admin/questions', [payload]);
      setQuestions([...questions, response.data[0]]);
      handleCloseAddQuestionModal();
      client.invalidateQueries(['questionnaires', questionnaireID]);
    } catch (error) {
      toastError(error);
    }
  };

  const handleUpdateQuestion = async (values: QuestionValues) => {
    try {
      const payload = getQuestionPayload(values, Number(questionnaireID));
      const { data } = await instance.put(
        `admin/questions/${values?.id}`,
        payload
      );

      setQuestions(
        questions.map(question => {
          if (question.id === values?.id) {
            return data;
          }
          return question;
        })
      );
      handleCloseEditQuestionModal();
      client.invalidateQueries(['questionnaires', questionnaireID]);
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteQuestion = async (id: number) => {
    try {
      await instance.delete(`admin/questions/${id}`);
      setQuestions(questions.filter(question => question.id !== id));
      handleCloseAddQuestionModal();
      client.invalidateQueries(['questionnaires', questionnaireID]);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    loadTypeOptions();
  }, []);


  useEffect(() => {
    if (questionnaire) {
      setValues({
        name: questionnaire?.name,
        for_novice: questionnaire?.for_novice,
        type: questionnaire?.questionnaire_type,
        publication_date: dayjs(questionnaire?.publication_date),
        removal_date: dayjs(questionnaire?.removal_date),
        questionnaire_accommodations:
          questionnaire?.questionnaire_accommodations,
        questionnaire_organizations: questionnaire?.questionnaire_organizations,
        licensor: questionnaire?.licensor,
        accommodation_all: false,
        organization_all: false,
        user_id: questionnaire?.users ? questionnaire.users : [],
        group_send: '1',
        show_name: questionnaire?.name_is_required,
        show_phone: questionnaire?.phone_is_required,
      });
    }
  }, []);

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.formInputs}>
        <div className={styles.group}>
          <InputBar
            name="name"
            label={t('anquette_name')}
            placeholder={t('enter_anquette_name')}
            value={values.name}
            disabled={false}
            star={true}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name && Boolean(errors.name)}
            errors={t(errors.name || '')}
          />

          <InputBar
            name="licensor"
            label={t('licensor_name')}
            placeholder={t('enter_licensor_name')}
            value={values.licensor}
            disabled={false}
            star={true}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.licensor && Boolean(errors.licensor)}
            errors={t(errors.licensor || '')}
          />
        </div>
        <div className={styles.group}>
          <div className={styles.group__item}>
            <CustomSelect
              label={t('questionnaire_subject')}
              placeholder={t('validate_subject')}
              value={values.type}
              onChange={value => setFieldValue('type', value)}
              options={typeOptions}
              classNameLabel={styles.typeSelect}
              required
              error={t(errors.type?.name || '')}
            />
          </div>
          <div className={styles.field}>
            <CustomDatePicker
              title={t('start_date_anquette')}
              placeholder={[t('date'), t('date')]}
              value={[values.publication_date, values.removal_date]}
              onBlur={() => setFieldTouched('publication_date', true)}
              onChange={(name, value) => {
                if (value) {
                  if (name === 'start_date') {
                    setFieldValue('publication_date', value);
                  } else if (name === 'end_date') {
                    setFieldValue('removal_date', value);
                  }
                } else {
                  setFieldValue('publication_date', null);
                  setFieldValue('removal_date', null);
                }
              }}
            />
          </div>
        </div>

        <div className={styles.selectWrappers}>
          <QuerySelect
            onChange={(value: number[]) => {
              setFieldValue('user_id', value);
              setSelectedUsers(value);
            }}
            value={selectedUsers}
            queryFn={({
              pageParam = 1,
              meta,
            }): Promise<PaginatedResponse<UserItem>> =>
              getUsers({
                sort: '',
                perPage: 100,
                field: '',
                query: meta?.searchQuery as any,
                page: pageParam,
              })
            }
            queryKey={['users', params]}
            star={true}
            label={t('select_recipient')}
            multiple={true}
            getItems={getUserById}
            error={t(errors.user_id as string)}
          />
        </div>

        <div className={styles.selectWrappers}>
          <CustomQuerySelect
            label={t('accommodation_facility')}
            onChange={value => {
              setFieldValue('questionnaire_accommodations', value);
              setFieldValue('questionnaire_organizations', []);
            }}
            value={values.questionnaire_accommodations}
            query="admin/accommodations"
            queryKey={[
              'accommodations',
              {
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
              },
            ]}
            params={{
              sort: '',
              perPage: 100,
              field: '',
              page: 1,
            }}
            required
            placeholder={t('to_choose')}
            multiselect
            isSearchEnabled
            error={t(errors.questionnaire_accommodations as string)}
            disabled={values.accommodation_all}
            hasCheckbox
            checkboxLabel={t('all_objects')}
            setIsCheckedAll={value => {
              setFieldValue('accommodation_all', value);
              setFieldValue('questionnaire_organizations', []);
            }}
          />
        </div>

        <div className={styles.selectWrappers}>
          <CustomQuerySelect
            label={t('organizations')}
            query="admin/organization"
            onChange={value => {
              setFieldValue('questionnaire_organizations', value);
            }}
            placeholder={t('validate_organizations')}
            value={values.questionnaire_organizations}
            isSearchEnabled
            queryKey={[
              'organizations',
              {
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
                accommodation_ids: values.questionnaire_accommodations.map(i => i.id),
              },
            ]}
            params={{
              sort: '',
              perPage: 100,
              field: '',
              page: 1,
              accommodation_ids: values.questionnaire_accommodations.map(i => i.id),
            }}
            required
            multiselect
            disabled={values.organization_all}
            error={t(errors.questionnaire_organizations as string)}
            hasCheckbox
            checkboxLabel={t('all_organizations')}
            setIsCheckedAll={value => setFieldValue('organization_all', value)}
          />
        </div>
      </div>

      <div className={styles.noviceWrapper}>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.for_novice}
            onChange={() => setFieldValue('for_novice', !values.for_novice)}
          />
          {t('sending_to_the_first_works')}
        </label>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.show_name}
            onChange={() => setFieldValue('show_name', !values.show_name)}
          />
          {t('show_name')}
        </label>
        <label className={styles.noviceLabel}>
          <CustomCheckbox
            isChecked={values.show_phone}
            onChange={() => setFieldValue('show_phone', !values.show_phone)}
          />
          {t('show_phone')}
        </label>
      </div>

      <div className={styles.questionsTableContainer}>
        <QuestionsList
          isSubmitting={isSubmitting}
          questions={questions}
          clickAddQuestion={handleOpenAddQuestionModal}
          clickDeleteQuestion={handleDeleteQuestion}
          clickEditButton={handleOpenEditQuestionModal}
        />
      </div>

      <div className={styles.formControl}>
        <div className={styles.submitContainer}>
          <Button
            type="submit"
            text={t('save_n_exit')}
            disabled={isSubmitting || !isValid}
          />
        </div>
        <div className={styles.submitContainer}>
          <Button
            onClick={() =>
              navigate(
                `/questionnaires/${questionnaireID}/results?${value}=${paginate}`
              )
            }
            type="button"
            text={t('questionnaire_results')}
            disabled={isSubmitting}
            className={styles.statsButton}
            Icon={StatIcon}
          />
        </div>
      </div>
      <Modal
        isOpen={isAddQuestionModalOpen}
        onClose={handleCloseAddQuestionModal}
        modalStyles={styles.questionModal}
      >
        <QuestionForm
          handleCloseCreate={handleCloseAddQuestionModal}
          clickAddQuestion={handleCreateQuestion}
          questions={questions}
        />
      </Modal>

      <Modal
        isOpen={isEditQuestionModalOpen}
        onClose={handleCloseEditQuestionModal}
        modalStyles={styles.questionModal}
      >
        {editingQuestion && (
          <EditQuestionForm
            question={editingQuestion}
            handleCloseCreate={handleCloseEditQuestionModal}
            clickUpdateQuestion={handleUpdateQuestion}
            questions={questions}
          />
        )}
      </Modal>
    </form>
  );
};

export default EditQuestionnarieForm;
